// =============================================================================
// Keep the iOS widget's copy of the session in step with the app's.
//
// The widgets can write straight to Supabase (ticking a to-do off from the
// home screen without opening the app) if they hold the user's session. The
// app mirrors every session change into the shared Keychain via the
// EuleaWidgetBridge plugin. Supabase rotates the refresh token on every
// refresh, so when the *widget* refreshed last, the app adopts that newer
// pair on foreground instead of failing with a stale one.
// =============================================================================

import { Capacitor, registerPlugin } from '@capacitor/core';
import { supabase } from './supabase';

const Bridge = Capacitor.isNativePlatform() ? registerPlugin('EuleaWidgetBridge') : null;

const toNative = (session) =>
  session
    ? {
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
        expiresAt: Number(session.expires_at) || 0,
        userId: session.user?.id || ''
      }
    : { accessToken: '', refreshToken: '', expiresAt: 0, userId: '' };

// Push the current session now and on every auth change. Returns an unsubscribe.
export const mirrorSessionToNative = () => {
  if (!Bridge) return () => {};
  const push = (session) => Bridge.setSession(toNative(session)).catch(() => {});
  supabase.auth.getSession().then(({ data }) => push(data.session));
  const { data } = supabase.auth.onAuthStateChange((_event, session) => push(session));
  return () => data.subscription.unsubscribe();
};

// If the widget holds a newer session than we do, take it over. Returns true
// when the app's session was replaced.
export const adoptNativeSession = async () => {
  if (!Bridge) return false;
  try {
    const theirs = await Bridge.getSession();
    if (!theirs?.refreshToken || !theirs.accessToken) return false;
    const { data } = await supabase.auth.getSession();
    const ours = data.session;
    if (!ours) return false;
    if (theirs.refreshToken === ours.refresh_token) return false;
    if ((Number(theirs.expiresAt) || 0) <= (Number(ours.expires_at) || 0)) return false;
    const { error } = await supabase.auth.setSession({
      access_token: theirs.accessToken,
      refresh_token: theirs.refreshToken
    });
    return !error;
  } catch {
    return false;
  }
};
